/**
 * Browser-side merge aligned with shared-functions `merge_user_data` (second argument wins on scalars).
 * Webpack forbids importing from `shared-functions/` — this module uses `complexKeys` + `listArrayKeys`
 * synced into `src/helpers/static_vars.js` (see scripts/sync_shared_static_vars.sh).
 * Dedup for complex arrays uses `uniqueProps` only, matching JS `merge_complex_data_types.js` (not Python hash keys).
 */

import cloneDeep from 'lodash.clonedeep';
import merge from 'lodash.merge';
import { complexKeys, listArrayKeys } from './static_vars';

/**
 * @param {Array} arr1 - priority / incoming
 * @param {Array} arr2 - base / existing
 * @param {string[]} uniqueProps
 * @returns {Array}
 */
function merge_and_deduplicate_object_arrays(arr1, arr2, uniqueProps) {
	const merged_items = {};

	const get_composite_key = (item) => {
		if (typeof item !== 'object' || item === null) {
			return null;
		}
		const key_parts = uniqueProps
			.map((prop) => item[prop])
			.filter((val) => val !== undefined && val !== null && val !== '');

		if (key_parts.length === 0) {
			return null;
		}
		return `key_${key_parts.join('|')}`;
	};

	(arr2 || []).forEach((item) => {
		const key = get_composite_key(item);
		if (key) {
			merged_items[key] = cloneDeep(item);
		}
	});

	(arr1 || []).forEach((item) => {
		const key = get_composite_key(item);
		if (key) {
			if (merged_items[key]) {
				merged_items[key] = merge(merged_items[key], item);
			} else {
				merged_items[key] = cloneDeep(item);
			}
		}
	});

	return Object.values(merged_items);
}

/**
 * @param {Object} incoming_data_fields - priority
 * @param {Object} existing_user_data - base
 * @returns {Promise<Object>}
 */
async function merge_complex_arrays_of_objects_browser(incoming_data_fields = {}, existing_user_data = {}) {
	if (
		incoming_data_fields === null ||
		typeof incoming_data_fields !== 'object' ||
		Array.isArray(incoming_data_fields)
	) {
		return {};
	}
	if (
		existing_user_data === null ||
		typeof existing_user_data !== 'object' ||
		Array.isArray(existing_user_data)
	) {
		existing_user_data = {};
	}

	const result_data_fields = cloneDeep(incoming_data_fields);

	complexKeys.forEach((item) => {
		const key = item.key;
		const unique_props = item.uniqueProps;
		const incoming_array = result_data_fields[key];
		const existing_array = existing_user_data[key];

		if (Array.isArray(incoming_array) || Array.isArray(existing_array)) {
			result_data_fields[key] = merge_and_deduplicate_object_arrays(
				incoming_array || [],
				existing_array || [],
				unique_props
			);
		}
	});

	return result_data_fields;
}

/**
 * Merge two person-shaped user_data objects; `new_user_data` wins on conflict.
 * @param {Object} original_user_data
 * @param {Object} new_user_data
 * @returns {Promise<Object>}
 */
export async function merge_user_data_browser(original_user_data = {}, new_user_data = {}) {
	const original_user_data_without_complex_arrays = {};
	const new_user_data_without_complex_arrays = {};
	const original_complex_arrays = {};
	const new_complex_arrays = {};

	Object.keys(original_user_data).forEach((key) => {
		if (complexKeys.some((item) => item.key === key) || listArrayKeys.includes(key)) {
			original_complex_arrays[key] = original_user_data[key];
		} else {
			original_user_data_without_complex_arrays[key] = original_user_data[key];
		}
	});

	Object.keys(new_user_data).forEach((key) => {
		if (complexKeys.some((item) => item.key === key) || listArrayKeys.includes(key)) {
			new_complex_arrays[key] = new_user_data[key];
		} else {
			new_user_data_without_complex_arrays[key] = new_user_data[key];
		}
	});

	const merged_regular_fields = merge(
		{},
		original_user_data_without_complex_arrays,
		new_user_data_without_complex_arrays
	);

	const merged_complex_arrays = await merge_complex_arrays_of_objects_browser(
		new_complex_arrays,
		original_complex_arrays
	);

	listArrayKeys.forEach((key) => {
		const original_list = original_complex_arrays[key];
		const new_list = new_complex_arrays[key];

		if (Object.prototype.hasOwnProperty.call(new_complex_arrays, key)) {
			merged_complex_arrays[key] = new_list;
		} else if (Object.prototype.hasOwnProperty.call(original_complex_arrays, key)) {
			merged_complex_arrays[key] = original_list;
		}
	});

	if (merged_complex_arrays.subscribedMessageTypeIds && merged_complex_arrays.unsubscribedMessageTypeIds) {
		const subscribed_set = new Set(merged_complex_arrays.subscribedMessageTypeIds);
		const unsubscribed_set = new Set(merged_complex_arrays.unsubscribedMessageTypeIds);
		unsubscribed_set.forEach((id) => {
			if (subscribed_set.has(id)) {
				subscribed_set.delete(id);
			}
		});
		merged_complex_arrays.subscribedMessageTypeIds = Array.from(subscribed_set);
	}

	return {
		...merged_regular_fields,
		...merged_complex_arrays,
	};
}
